// Support gate (IMPLEMENTATION §0.5 "Minimum platform"): iOS 16+ / Android Chrome 107+ and WebGL.
// Older browsers, or no WebGL (unless ?render=none), get the ErrorScreen.

export interface SupportResult {
  ok: boolean;
  webgl: boolean;
  missing: string[];
}

/** CSS feature probe; false when CSS.supports is missing or throws. */
function cssSupports(win: Window & typeof globalThis, prop: string, value: string): boolean {
  try {
    return typeof win.CSS?.supports === 'function' && win.CSS.supports(prop, value);
  } catch {
    return false;
  }
}

/**
 * Feature probes for the iOS 16 / Chrome 107 floor. Each entry: [name, present?]. The JS/DOM probes all
 * shipped by Safari 15.4 / Chrome 98, so CSS container queries (Safari 16.0, Chrome 105) set the floor:
 * iOS ≤ 15.x is refused; Chrome 105–106 slips through (no reliable 107-only probe; negligible share).
 */
function featureChecks(win: Window & typeof globalThis): Array<[string, boolean]> {
  return [
    ['PointerEvent', typeof win.PointerEvent === 'function'],
    ['Element.setPointerCapture', typeof win.Element?.prototype.setPointerCapture === 'function'],
    ['Array.prototype.at', typeof Array.prototype.at === 'function'],
    ['Array.prototype.findLast', typeof Array.prototype.findLast === 'function'],
    ['Object.hasOwn', typeof Object.hasOwn === 'function'],
    ['structuredClone', typeof win.structuredClone === 'function'],
    ['CSS.supports', typeof win.CSS?.supports === 'function'],
    ['CSS container queries (iOS 16+)', cssSupports(win, 'container-type', 'inline-size')],
  ];
}

/** WebGL 1 (what Phaser 4 requests). Frees the probe context right away. */
export function probeWebGL(doc: Document = document): boolean {
  try {
    const c = doc.createElement('canvas');
    const gl = c.getContext('webgl');
    if (!gl) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

export function checkSupport(
  needWebGL: boolean,
  win: Window & typeof globalThis = window,
): SupportResult {
  const missing = featureChecks(win)
    .filter(([, ok]) => !ok)
    .map(([name]) => name);
  const webgl = needWebGL ? probeWebGL(win.document) : false;
  if (needWebGL && !webgl) missing.push('WebGL');
  return { ok: missing.length === 0, webgl, missing };
}

/** Plain-DOM ErrorScreen: the fallback when the Preact one (ui/overlays/ErrorScreen.tsx, M11) cannot render. */
export function showErrorScreen(title: string, detail: string, doc: Document = document): void {
  const splash = doc.getElementById('splash');
  const host = splash ?? doc.body;
  host.classList.remove('gone');
  host.textContent = '';
  const box = doc.createElement('div');
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'max-width:320px;padding:24px;text-align:center;font-size:16px;font-weight:400;';
  const h = doc.createElement('div');
  h.style.cssText = 'font-size:22px;font-weight:700;margin-bottom:12px;';
  h.textContent = title;
  const p = doc.createElement('div');
  p.textContent = detail;
  box.append(h, p);
  host.appendChild(box);
}
